/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {WindowSizeService} from '../../../services/window-size.service';
import {ActivatedRoute, Router} from '@angular/router';
import {GeneralDataHandlingService} from '../../../services/general.data-handling.service';
import {FormCategory, FormFieldAttribute} from '../../../models/FormCategory';
import {PassportService} from '../../../services/passport.service';
import {SchemaDTO, UiFavoritesDTO, UISchemaDTO} from '../../../models/Schema';
import {PassportDto} from '../../../models/Passport';
import {firstValueFrom, Subscription} from 'rxjs';
import {PassportTranslationService} from '../../../services/passport-translation.service';
import {ContentLanguageSelectionService} from '../../../services/content-language-selection.service';

const DEFAULT_PASSPORT_IMAGE = 'Qstor_Header_Website-jpg_Rendition1920.jpg';

@Component({
  selector: 'app-pass-viewer',
  templateUrl: './pass-viewer.component.html',
  styleUrl: './pass-viewer.component.scss',
  standalone: false,
})
export class PassViewerComponent implements OnInit, OnChanges, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly passportService = inject(PassportService);
  private readonly generalDataHandlingService = inject(
    GeneralDataHandlingService,
  );
  private readonly passportTranslation = inject(PassportTranslationService);
  private readonly contentLanguageSelection = inject(
    ContentLanguageSelectionService,
  );

  @Input() passport?: PassportDto['data'];
  @Input() showTabs = true;
  @Input() schema: SchemaDTO['data'];
  @Input() uiSchema: UISchemaDTO['data'] | undefined;

  detailCardContent: {
    icon: string;
    label: string;
    value?: string | number;
  }[] = [];

  passportImage = DEFAULT_PASSPORT_IMAGE;
  changeLangSubscription: Subscription;

  batteryPassCategoryIcons: Record<string, string> = {
    dppHeader: 'info',
    generalProductInformation: 'product',
    labeling: 'label',
    carbonFootprint: 'leaf',
    supplyChainDueDiligence: 'analyze',
    materialComposition: 'layers',
    circularity: 'cycle',
    performance: 'gauge',
  };
  categories: FormCategory[] = [];
  selectedTabIndex = 0;

  protected readonly WindowSizeService = WindowSizeService;

  async ngOnInit() {
    this.initContentLanguage();

    this.categories = await this.passportService.loadMappedSchema(
      this.schema.id,
    );
    this.passportImage =
      '/assets/img/' +
      (this.uiSchema?.['ui:passportImage'] || DEFAULT_PASSPORT_IMAGE);
    this.setSelectedTabIndex();
    await firstValueFrom(
      this.passportTranslation.load(this.schema.id, this.translate.currentLang),
    );
    await this.setDetailCards();
    this.changeLangSubscription = this.translate.onLangChange.subscribe(
      (event) => {
        this.passportTranslation.load(this.schema.id, event.lang).subscribe({
          complete: () => this.reprocessForLanguage(),
        });
      },
    );
  }

  ngOnDestroy() {
    if (this.changeLangSubscription) {
      this.changeLangSubscription.unsubscribe();
    }
  }

  /**
   * Re-maps the schema categories/sub-categories and rebuilds the favorite
   * detail cards for the currently active language once ngx-translate and
   * the passport schema translations have finished loading. Without this,
   * category and attribute labels would keep showing the previously loaded
   * language until a full page reload.
   */
  private async reprocessForLanguage(): Promise<void> {
    this.categories = await this.passportService.loadMappedSchema(
      this.schema.id,
    );
    await this.setDetailCards();
  }

  /**
   * Starts a fresh content-language session when the parent swaps in another
   * passport without re-creating this component (e.g. the private viewer
   * reacting to a route parameter change), so no selection carries over.
   *
   * @param changes - The changed inputs.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const passportChange = changes['passport'];
    if (passportChange && !passportChange.firstChange) {
      this.initContentLanguage();
    }
  }

  /**
   * Clears every content-language selection and selects the browser's
   * preferred language, once per passport view.
   */
  private initContentLanguage(): void {
    this.contentLanguageSelection.reset();
    const preferredLanguage = this.resolvePreferredLanguage();
    if (preferredLanguage) {
      this.contentLanguageSelection.switchAll(preferredLanguage);
    }
  }

  /**
   * Reads the browser's preferred language directly from `navigator`
   * (never `TranslateService`/UI-language services) and normalizes it to
   * its lowercase ISO 639-1 primary subtag (for example `en-US` -> `en`).
   *
   * @returns The normalized language code, or `null` if the browser did not report one.
   */
  private resolvePreferredLanguage(): string | null {
    const rawLanguage = navigator.language || navigator.languages?.[0];
    if (!rawLanguage) {
      return null;
    }
    const primarySubtag = rawLanguage.split('-')[0].toLowerCase();
    return primarySubtag || null;
  }

  setSelectedTabIndex() {
    this.route.queryParams.subscribe((params) => {
      const index = Number(params['categoryTab'] ?? 0);
      if (!isNaN(index) && 0 <= index && index <= this.categories.length - 1) {
        this.selectedTabIndex = index;
      } else {
        this.selectedTabIndex = 0;
      }
    });
  }

  async changeTab(index: number) {
    this.selectedTabIndex = index;
    await this.router.navigate([], {
      queryParams: {
        categoryTab: index,
      },
      queryParamsHandling: 'merge',
    });
  }

  async setDetailCards() {
    this.detailCardContent = [];
    const uiFavorites = this.uiSchema?.['ui:favorites'];
    if (!Array.isArray(uiFavorites)) {
      return;
    }

    uiFavorites.forEach((item) => {
      const schemaItem = this.resolveFavoriteSchemaItem(item);
      const value = this.resolveFavoriteValue(item, schemaItem);

      this.detailCardContent.push({
        icon: item.icon,
        label: this.resolveFavoriteLabel(item, schemaItem),
        value,
      });
    });
  }

  private resolveFavoriteSchemaItem(
    item: UiFavoritesDTO,
  ): FormFieldAttribute | null {
    if (!item.valuePath) {
      return null;
    }

    const schemaItems = this.generalDataHandlingService.findAttributeWithValue(
      this.categories,
      item.valuePath,
      'valuePath',
    );
    return schemaItems[0] ?? null;
  }

  private resolveFavoriteLabel(
    item: UiFavoritesDTO,
    schemaItem: FormFieldAttribute | null,
  ): string {
    if (item.label) {
      return this.translate.instant(item.label) || item.label;
    }

    if (schemaItem) {
      return this.passportTranslation.translateByPath(
        schemaItem.valuePath,
        this.schema?.id,
        schemaItem.translation.label,
      );
    }

    return '';
  }

  private resolveFavoriteValue(
    item: UiFavoritesDTO,
    schemaItem: FormFieldAttribute | null,
  ): string | number | undefined {
    let value: string | number | undefined;

    if (schemaItem) {
      value = this.getValue(schemaItem);
      const unit = this.getUnit(schemaItem);
      if (value !== undefined && value !== '' && unit) {
        value = `${value} ${unit}`;
      }
    } else if (item.valuePath) {
      value = this.normalizeFavoriteValue(
        this.generalDataHandlingService.getValueByPath(
          this.passport,
          item.valuePath,
        ),
      );
    }

    return value ?? item.defaultValue;
  }

  private normalizeFavoriteValue(
    rawValue: unknown,
  ): string | number | undefined {
    if (typeof rawValue === 'string') {
      return rawValue.length > 0 ? rawValue : undefined;
    }

    if (typeof rawValue === 'number') {
      return rawValue;
    }

    if (typeof rawValue === 'boolean') {
      return rawValue.toString();
    }

    return undefined;
  }

  getUnit(item: FormFieldAttribute): string {
    const unit = item?.unit;
    if (unit) {
      const translation = this.translate.instant(`general.units.${unit}`);
      return translation === `general.units.${unit}` ? unit : translation;
    }
    return '';
  }

  getValue(item: FormFieldAttribute): string | number | undefined {
    let value =
      this.generalDataHandlingService.getValueByPath(
        this.passport,
        item.valuePath,
      ) || undefined;

    if (item.type === 'select') {
      const selectedOption = item.options?.find(
        (option) => option.value === value,
      );
      if (selectedOption) {
        value = this.translate.instant(selectedOption.label);
      }
    }
    return value;
  }

  isOverflown(elementId: string): boolean {
    const element = document.getElementById(elementId);
    if (!element) {
      return false;
    }
    const widthDiff = element.scrollWidth - element.clientWidth;
    return widthDiff > 1;
  }

  shouldShowEmptyState(): boolean {
    return !this.uiSchema || this.categories.length === 0;
  }
}
